import React from 'react';
import { Button, Flex, Popover, PopoverBody, PopoverContent, PopoverTrigger, Tag, Text } from '@chakra-ui/react';
import {
    MoreIcon,
    TrashcanIcon,
    EditIcon2,
    EyeIcon,
    CheckMarkIcon,
    DownloadIcon,
    DoubleCheckMarkIcon,
    EmptyStateImage,
} from '@assets/icons.js';
import { useConfirmModal } from '@store/useConfirmModal';
import { useEditDetail } from '@pages/ConsumerPromotion/hooks/state/useEditDetail';
import { useNavigate } from 'react-router-dom';
import useMutateCPList from '@pages/ConsumerPromotion/hooks/mutate/useMutateCPList';
import { fileDownloader, formatCurrency } from '@helpers/utils';
import { useSnackbarNotification } from '@store/snackbarNotification';
import usePaginationStore from '@pages/ConsumerPromotion/hooks/state/usePaginationCust';

const renderComp = (value) => <Text>{value ?? ''}</Text>;

export const tableList = [
    {
        title: 'Customer ID',
        value: 'customer_id',
        width: 160,
        component: ({ value }) => renderComp(value),
    },
    {
        title: 'Customer Name',
        value: 'name',
        width: 220,
        component: ({ value }) => renderComp(value),
    },
    {
        title: 'Channel Report',
        value: 'channel_report_name',
        width: 150,
        component: ({ value }) => renderComp(value),
    },
    {
        title: 'Distribution Center',
        value: 'dc_name',
        width: 150,
        component: ({ value }) => renderComp(value),
    },
    {
        title: 'Sales Area',
        value: 'sales_area_name',
        width: 150,
        component: ({ value }) => renderComp(value),
    },
    {
        title: 'Sellout Vol.',
        value: 'sellout_volume',
        width: 90,
        component: ({ value }) => renderComp(value),
    },
    {
        title: 'Sellout Value',
        value: 'sellout_value',
        width: 100,
        component: ({ value }) => {
            const formattedValue = formatCurrency(value);
            return renderComp(formattedValue);
        },
    },
    {
        title: 'Status',
        value: 'status',
        width: 136,
        component: ({ value }) => {
            const lower = value?.toLowerCase?.();
            const isClaim = lower === 'claim';
            const isDone = lower === 'done';

            const normalizedValue = isClaim ? 'Claimed' : value;
            const variant = isClaim || isDone ? 'done' : 'draft';

            const icon = isDone ? <DoubleCheckMarkIcon size={16} /> : isClaim ? <CheckMarkIcon size={16} /> : null;

            return (
                <Flex h="40px" alignItems="center">
                    <Tag variant={variant} textTransform="capitalize" w="fit-content" maxW="fit-content">
                        {icon ? (
                            <Flex gap="4px" alignItems="center">
                                {icon} {normalizedValue}
                            </Flex>
                        ) : (
                            normalizedValue
                        )}
                    </Tag>
                </Flex>
            );
        },
    },
    {
        title: <Text fontWeight={700}>Action</Text>,
        key: 'action',
        value: 'id',
        width: 66,
        component: (props) => {
            const stateEdit = useEditDetail();
            const stateConfirm = useConfirmModal();
            const navigate = useNavigate();
            const { claimPromoByCust, completePromoByCust } = useMutateCPList();
            const { onChangeSnackbarNotification } = useSnackbarNotification();
            const { deleteItem } = usePaginationStore();

            const lowerStatus = props?.status?.toLowerCase?.();
            const promoStatus = stateEdit.overviewData?.promotion_status?.status?.toLowerCase?.();

            const isReadyToClaim = promoStatus === 'ready to claim';
            const isClaimed = lowerStatus !== 'not claim';
            const isCompleted = lowerStatus === 'done';

            const hanldeEdit = () => {
                stateEdit.set({
                    editingRowCustomer: props,
                    searchMasterDataCustomer: props.name,
                });
            };

            const hanldeDelete = () => {
                stateConfirm.set({
                    isOpen: true,
                    title: 'Hapus Customer?',
                    desc: 'Data yang telah dituliskan akan hilang, perlu melakukan pengisian ulang untuk menambahkan customer.',
                    onSubmit: () => {
                        return deleteItem(props.id);
                    },
                });
            };

            const hanldeView = () => {
                navigate(`customer/${props.id}`);
            };

            const handleClaim = () => {
                stateConfirm.set({
                    isOpen: true,
                    title: 'Claim Promotion?',
                    desc: 'Claim Promotion untuk customer ini.',
                    btnLabelNo: 'Nanti',
                    btnLabelYes: 'Claim',
                    onSubmit: () => {
                        stateEdit.set({
                            claimPromo: {
                                idCust: props.id,
                                idPromo: stateEdit?.id,
                                name: props.name,
                            },
                        });
                        claimPromoByCust.mutate({ id: { promo: stateEdit?.id, cust: props.customer_id } });
                    },
                });
            };

            const handleDownloadData = async () => {
                stateEdit.set({
                    claimPromo: {
                        idCust: props.id,
                        idPromo: stateEdit?.id,
                    },
                });

                try {
                    await fileDownloader({
                        url: `/api/v1/promotion-claim/${props?.id}/claim/customer-export`,
                        fileName: `Data Claim Promotion ${props.name}.pdf`,
                    });

                    onChangeSnackbarNotification({
                        status: 'success',
                        description: `Data Claim ${props.name} didownload!`,
                        duration: 3,
                    });
                } catch (error) {
                    if (error.response && error.response.data instanceof Blob) {
                        const text = await error.response.data.text();
                        try {
                            const json = JSON.parse(text);
                            onChangeSnackbarNotification({
                                status: 'error',
                                title: 'Gagal download data!',
                                description: `${json?.message}`,
                                duration: 3,
                            });
                        } catch {
                            console.error('Error (not JSON):', text);
                        }
                    } else {
                        const msg = error?.response?.data?.message;
                        onChangeSnackbarNotification({
                            status: 'error',
                            title: 'Gagal download data!',
                            description: `${msg}`,
                            duration: 3,
                        });
                    }
                } finally {
                    stateEdit.set({
                        claimPromo: null,
                    });
                }
            };

            const handleCompletePromo = async () => {
                stateConfirm.set({
                    isOpen: true,
                    title: 'Complete This Customer Process?',
                    desc: 'Pastikan jika proses pengiriman produk Non Jual telah dilakukan untuk customer ini.',
                    btnLabelNo: 'Nanti',
                    btnLabelYes: 'Complete',
                    ConfirmationStateImage: EmptyStateImage,
                    onSubmit: () => {
                        completePromoByCust.mutate({ id: { promo: stateEdit?.id, cust: props.customer_id } });
                    },
                });
            };

            return (
                <Popover placement="bottom-start">
                    <PopoverTrigger>
                        <Button variant={'unstyled'} justifySelf={'end'} px={0} minW={'10px'}>
                            <MoreIcon />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent>
                        {stateEdit.isEditCustomer ? (
                            <>
                                <PopoverBody w={'full'} _hover={{ bg: 'sagitarius.100' }}>
                                    <Button variant={'unstyled'} onClick={hanldeEdit} color={'sagitarius.500'}>
                                        <EditIcon2 size={18} />
                                        Edit
                                    </Button>
                                </PopoverBody>
                                <PopoverBody _hover={{ bg: 'danger.100' }}>
                                    <Button variant={'unstyled'} onClick={hanldeDelete} color={'danger.500'}>
                                        <TrashcanIcon size={18} />
                                        Delete
                                    </Button>
                                </PopoverBody>
                            </>
                        ) : (
                            <>
                                <PopoverBody w={'full'} _hover={{ bg: 'sagitarius.100' }}>
                                    <Button variant={'unstyled'} onClick={hanldeView} color={'sagitarius.500'}>
                                        <EyeIcon size={18} />
                                        View
                                    </Button>
                                </PopoverBody>

                                {isReadyToClaim && !isClaimed && (
                                    <PopoverBody w={'full'} _hover={{ bg: 'sagitarius.100' }}>
                                        <Button variant={'unstyled'} onClick={handleClaim} color={'sagitarius.500'}>
                                            <CheckMarkIcon size={18} color="#005b99" />
                                            Claim
                                        </Button>
                                    </PopoverBody>
                                )}

                                {isClaimed && (
                                    <PopoverBody w={'full'} _hover={{ bg: 'sagitarius.100' }}>
                                        <Button
                                            variant={'unstyled'}
                                            onClick={handleDownloadData}
                                            color={'sagitarius.500'}
                                        >
                                            <DownloadIcon size={18} color="#005b99" />
                                            Data
                                        </Button>
                                    </PopoverBody>
                                )}

                                {isCompleted && (
                                    <PopoverBody w={'full'} _hover={{ bg: 'sagitarius.100' }}>
                                        <Button
                                            variant={'unstyled'}
                                            onClick={handleCompletePromo}
                                            color={'sagitarius.500'}
                                        >
                                            <DoubleCheckMarkIcon size={18} color="#005b99" />
                                            Completed
                                        </Button>
                                    </PopoverBody>
                                )}
                            </>
                        )}
                    </PopoverContent>
                </Popover>
            );
        },
    },
];
